import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ComicCard from '@/components/base/ComicCard';
import { comicCategories, comics } from '@/mocks/comics';
import { useFavorites } from '@/hooks/useFavorites';
import { formatViews } from '@/utils/comic';

export default function DetailPage() {
  const { comicId = '' } = useParams();
  const navigate = useNavigate();
  const comic = comics.find((item) => item.id === comicId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [chapterAsc, setChapterAsc] = useState(false);
  const [toast, setToast] = useState('');

  const related = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((item) => item.id !== comic.id && item.categories.some((id) => comic.categories.includes(id)))
      .slice(0, 6);
  }, [comic]);

  if (!comic) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-emotion-sad-line" />
        </span>
        <p className="text-foreground-700">没有找到这部漫画，可能链接已失效</p>
        <Link
          to="/list"
          className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
        >
          返回漫画库
        </Link>
      </div>
    );
  }

  const chapters = chapterAsc ? comic.chapters : [...comic.chapters].reverse();
  const fav = isFavorite(comic.id);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2200);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}/detail/${comic.id}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('链接已复制，快分享给朋友吧');
    } catch {
      showToast('链接已生成，请手动复制地址栏链接');
    }
  };

  const handleFavorite = () => {
    const added = toggleFavorite(comic.id);
    showToast(added ? '已加入收藏，可在「收藏」查看' : '已取消收藏');
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8">
      <title>{`${comic.title} - ${comic.author} - 抖漫`}</title>
      <meta name="description" content={comic.description.slice(0, 140)} />

      <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/category/${comic.categories[0]}`} className="transition hover:text-primary-600">
          {comicCategories.find((item) => item.id === comic.categories[0])?.name ?? '分类'}漫画
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">{comic.title}</span>
      </nav>

      <section className="grid grid-cols-1 gap-8 md:grid-cols-[280px_1fr]">
        <div className="mx-auto w-[220px] overflow-hidden rounded-lg border border-background-200 bg-background-100 md:mx-0 md:w-full">
          <img
            src={comic.cover}
            alt={`${comic.title} 漫画封面`}
            title={`${comic.title} 在线漫画`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              {comic.status}
            </span>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              {comic.region}
            </span>
          </div>

          <h1 className="mt-3 font-heading text-3xl text-foreground-950">{comic.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-quill-pen-line text-primary-500" />
              作者：{comic.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-accent-500" />
              评分：{comic.rating.toFixed(1)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-primary-500" />
              人气：{formatViews(comic.views)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-secondary-500" />
              更新：{comic.updatedAt}
            </span>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {comic.categories.map((id) => {
              const category = comicCategories.find((item) => item.id === id);
              if (!category) return null;
              return (
                <Link
                  key={id}
                  to={`/category/${id}`}
                  className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900 transition hover:bg-accent-200"
                >
                  <i className={category.icon} />
                  {category.name}
                </Link>
              );
            })}
          </div>

          <p className="mt-4 flex flex-wrap gap-2 text-xs text-foreground-500">
            {comic.tags.map((tag) => (
              <span key={tag} className="rounded-md bg-background-100 px-2 py-0.5">
                #{tag}
              </span>
            ))}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/read/${comic.id}/${comic.chapters[0].id}`)}
              className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-book-open-line text-base" />
              开始阅读
            </button>
            <button
              type="button"
              onClick={handleFavorite}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-6 py-3 text-sm font-medium transition cursor-pointer ${
                fav
                  ? 'border-primary-500 bg-primary-50 text-primary-600'
                  : 'border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600'
              }`}
            >
              <i className={fav ? 'ri-heart-fill text-base' : 'ri-heart-line text-base'} />
              {fav ? '已收藏' : '收藏'}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm font-medium text-foreground-700 transition hover:border-primary-400 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-share-line text-base" />
              分享
            </button>
          </div>
        </div>
      </section>

      <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="mb-3 font-heading text-xl text-foreground-950">漫画简介</h2>
        <p className="text-sm leading-relaxed text-foreground-700">{comic.description}</p>
      </section>

      <section className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl text-foreground-950">
            最新章节 <span className="text-sm font-normal text-foreground-500">（共 {comic.chapters.length} 话）</span>
          </h2>
          <button
            type="button"
            onClick={() => setChapterAsc((prev) => !prev)}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-xs text-foreground-700 transition hover:border-primary-400 hover:text-primary-600 cursor-pointer"
          >
            <i className="ri-swap-line" />
            {chapterAsc ? '正序' : '倒序'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {chapters.map((chapter) => (
            <Link
              key={chapter.id}
              to={`/read/${comic.id}/${chapter.id}`}
              className="truncate rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-700 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600"
            >
              {chapter.title}
            </Link>
          ))}
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl text-foreground-950">
            <i className="ri-magic-line text-primary-500" />
            相关推荐
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {related.map((item) => (
              <ComicCard key={item.id} comic={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 animate-fadeUp rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}